<script setup lang="ts">
import { computed } from "vue";
import type { WidgetProps } from "./contract";

/**
 * A rating, as stars or hearts.
 *
 * Stored as an integer, bounded by `config.max` — and the SAME bound exists as a
 * CHECK on the column, so an artifact or an automation writing 11 is refused
 * too. A widget-only limit is a limit until something else writes.
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const max = computed(() => {
	const raw = Number(props.field.config.max);
	return Number.isFinite(raw) ? Math.min(Math.max(Math.trunc(raw), 1), 20) : 5;
});
const icon = computed(() => (props.field.config.icon === "heart" ? "♥" : "★"));
const value = computed(() => Number(props.modelValue ?? 0));

function pick(next: number): void {
	// Clicking the current value clears it. Otherwise an optional rating chosen
	// by accident can never be taken back.
	emit("update:modelValue", value.value === next ? null : next);
}
</script>

<template>
	<div
		:id="controlId"
		class="flex items-center gap-1"
		role="radiogroup"
		:aria-label="field.label"
		:aria-invalid="Boolean(error)"
		:aria-describedby="describedBy"
	>
		<button
			v-for="n in max"
			:key="n"
			type="button"
			:disabled="disabled"
			:aria-label="`${n} de ${max}`"
			:aria-pressed="value >= n"
			class="text-xl leading-none transition-colors disabled:opacity-50"
			:class="value >= n ? 'text-warning' : 'text-fg-subtle hover:text-fg-muted'"
			@click="pick(n)"
		>
			{{ icon }}
		</button>
		<span class="ml-2 text-xs text-fg-subtle">{{ value || "—" }}</span>
	</div>
</template>
